import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, CheckCircle2 } from 'lucide-react';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { TextField, FormError, SubmitButton } from '@/components/auth/FormFields';
import { forgotPassword, readApiError } from '@/lib/auth-api';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError(t('auth.errors.emailInvalid'));
      return;
    }
    setEmailError(undefined);
    setLoading(true);
    setError(null);
    try {
      await forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(readApiError(err, t('auth.errors.generic')));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout
        title={t('auth.forgot.sentTitle')}
        subtitle={t('auth.forgot.sentBody', { email })}
        footer={
          <p className="text-center">
            <Link to="/login" className="font-semibold text-primary hover:underline">
              {t('auth.forgot.backToLogin')}
            </Link>
          </p>
        }
      >
        <div className="rounded-2xl border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-500/10 p-4 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
          <p className="text-sm text-emerald-700 dark:text-emerald-300">{t('auth.forgot.checkInbox')}</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.forgot.title')}
      subtitle={t('auth.forgot.subtitle')}
      footer={
        <p className="text-center">
          {t('auth.forgot.remembered')}{' '}
          <Link to="/login" className="font-semibold text-primary hover:underline">
            {t('auth.forgot.signIn')}
          </Link>
        </p>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <FormError message={error} />
        <TextField
          label={t('auth.fields.email')}
          type="email"
          autoComplete="email"
          placeholder={t('auth.placeholders.email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={emailError}
          leadingIcon={<Mail className="h-4 w-4" />}
          required
        />
        <SubmitButton loading={loading}>{t('auth.forgot.submit')}</SubmitButton>
      </form>
    </AuthLayout>
  );
}
